import { useEffect, useState } from "react";
import { X, Sparkles, AlertTriangle, Check } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

const REASONS = [
  { id: "too_expensive", label: "Too expensive" },
  { id: "missing_features", label: "Missing features I need" },
  { id: "switching_tools", label: "Switching to another tool" },
  { id: "not_using", label: "Not using it enough" },
  { id: "bugs_or_quality", label: "Bugs / quality issues" },
  { id: "temporary_pause", label: "Just need to pause for a while" },
  { id: "other", label: "Other" },
];

/**
 * 3-step cancel flow:
 *   1. offer  — one-click downsell to annual (skipped if user has no offer
 *               or is already on annual)
 *   2. reason — required reason picker + optional feedback
 *   3. done   — confirmation + "Undo" CTA inside grace period
 */
export default function CancelSubscriptionModal({ open, onClose, onCancelled }) {
  const [stage, setStage] = useState("offer"); // offer | reason | done
  const [intent, setIntent] = useState(null);
  const [reason, setReason] = useState("");
  const [feedback, setFeedback] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStage("offer");
    setReason("");
    setFeedback("");
    setBusy(false);
    api
      .post("/billing/cancel/intent")
      .then((r) => {
        setIntent(r.data);
        // Skip the offer step if it's not eligible (already annual / free).
        if (!r.data?.offer_eligible) setStage("reason");
        if (r.data?.pending_cancellation) setStage("done");
      })
      .catch((e) => {
        toast.error(formatApiError(e?.response?.data?.detail) || "Failed to open cancel flow.");
        onClose?.();
      });
  }, [open, onClose]);

  if (!open) return null;

  const acceptOffer = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/cancel/retain", {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not start checkout.");
      setBusy(false);
    }
  };

  const declineOffer = () => setStage("reason");

  const submitReason = async () => {
    if (!reason) {
      toast.error("Please pick a reason — it helps us improve.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/billing/cancel/confirm", { reason, feedback: feedback.trim() || null });
      setIntent({ ...intent, pending_cancellation: true, cancellation_effective_at: data.effective_at });
      setStage("done");
      toast.success("Subscription cancelled. You'll keep access until the end of your period.");
      onCancelled?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const undo = async () => {
    setBusy(true);
    try {
      await api.post("/billing/cancel/undo");
      toast.success("Cancellation reversed — welcome back!");
      onCancelled?.();
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4"
      data-testid="cancel-subscription-modal"
    >
      <div className="bg-white rounded-lg w-full max-w-lg overflow-hidden shadow-xl">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <h2 className="font-bold text-slate-900">
            {stage === "offer" && "Wait — save 20% before you go"}
            {stage === "reason" && "Why are you cancelling?"}
            {stage === "done" && "Subscription cancelled"}
          </h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100" data-testid="cancel-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* STAGE 1 — OFFER */}
        {stage === "offer" && intent?.offer && (
          <div className="p-6 space-y-4" data-testid="cancel-modal-offer">
            <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-md p-4">
              <Sparkles className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-bold text-slate-900">
                  Switch to yearly and save ${Math.round(intent.offer.savings)} on{" "}
                  {intent.offer.plan_name}
                </div>
                <div className="text-xs text-slate-600 mt-1">
                  Lock in <span className="font-semibold">${intent.offer.annual_per_month.toFixed(0)}/mo</span>{" "}
                  (instead of ${intent.offer.monthly_amount.toFixed(0)}/mo) for the next 12 months.
                </div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 text-center text-sm">
              <div className="p-3 rounded-md bg-slate-50">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Today</div>
                <div className="text-xl font-bold text-slate-900 mt-1 line-through opacity-60">
                  ${Math.round(intent.offer.monthly_amount * 12)}
                </div>
                <div className="text-[10px] text-slate-500">/yr standard</div>
              </div>
              <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200">
                <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold">If you stay</div>
                <div className="text-xl font-bold text-emerald-800 mt-1">${Math.round(intent.offer.annual_amount)}</div>
                <div className="text-[10px] text-emerald-700">/yr · {intent.offer.discount_pct}% off</div>
              </div>
              <div className="p-3 rounded-md bg-slate-50">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">You save</div>
                <div className="text-xl font-bold text-slate-900 mt-1">${Math.round(intent.offer.savings)}</div>
                <div className="text-[10px] text-slate-500">first year</div>
              </div>
            </div>
            <div className="flex flex-col gap-2 mt-2">
              <button
                onClick={acceptOffer}
                disabled={busy}
                data-testid="cancel-modal-accept-offer"
                className="w-full py-2.5 rounded-md bg-emerald-700 text-white text-sm font-semibold hover:bg-emerald-800 disabled:bg-slate-400"
              >
                {busy ? "Redirecting…" : `Keep ${intent.offer.plan_name} for $${Math.round(intent.offer.annual_per_month)}/mo`}
              </button>
              <button
                onClick={declineOffer}
                disabled={busy}
                data-testid="cancel-modal-decline-offer"
                className="w-full py-2 text-xs text-slate-500 hover:text-slate-900"
              >
                No thanks, continue cancelling
              </button>
            </div>
          </div>
        )}

        {/* STAGE 2 — REASON */}
        {stage === "reason" && (
          <div className="p-6 space-y-4" data-testid="cancel-modal-reason">
            <div className="flex items-start gap-3 bg-slate-50 border border-slate-200 rounded-md p-3">
              <AlertTriangle className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" />
              <div className="text-xs text-slate-600">
                You'll keep access to <span className="font-semibold">{intent?.plan_name || "your plan"}</span> until
                the end of your billing period. Help us improve by telling us why you're leaving.
              </div>
            </div>
            <div className="space-y-1.5">
              {REASONS.map((r) => (
                <label
                  key={r.id}
                  className={`flex items-center gap-3 px-3 py-2.5 border rounded-md cursor-pointer ${
                    reason === r.id ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:bg-slate-50"
                  }`}
                  data-testid={`cancel-modal-reason-${r.id}`}
                >
                  <input
                    type="radio"
                    name="cancel-reason"
                    value={r.id}
                    checked={reason === r.id}
                    onChange={() => setReason(r.id)}
                    className="accent-slate-900"
                  />
                  <span className="text-sm text-slate-900">{r.label}</span>
                </label>
              ))}
            </div>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              rows={3}
              maxLength={2000}
              placeholder="What could we have done better? (optional)"
              data-testid="cancel-modal-feedback"
              className="w-full px-3 py-2 border border-slate-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 resize-none"
            />
            <div className="flex items-center justify-between">
              <button
                onClick={() => intent?.offer_eligible ? setStage("offer") : onClose?.()}
                className="text-sm text-slate-500 hover:text-slate-900"
              >
                ← Back
              </button>
              <button
                onClick={submitReason}
                disabled={busy || !reason}
                data-testid="cancel-modal-submit-reason"
                className="px-4 py-2 rounded-md bg-rose-700 text-white text-sm font-semibold hover:bg-rose-800 disabled:bg-slate-300"
              >
                {busy ? "Cancelling…" : "Cancel subscription"}
              </button>
            </div>
          </div>
        )}

        {/* STAGE 3 — DONE */}
        {stage === "done" && (
          <div className="p-6 space-y-4 text-center" data-testid="cancel-modal-done">
            <div className="w-12 h-12 mx-auto rounded-full bg-slate-100 flex items-center justify-center">
              <Check className="w-6 h-6 text-slate-700" />
            </div>
            <div className="text-sm text-slate-600">
              Your <span className="font-semibold">{intent?.plan_name || "subscription"}</span> will end on{" "}
              <span className="font-semibold text-slate-900">
                {intent?.cancellation_effective_at
                  ? new Date(intent.cancellation_effective_at).toLocaleDateString([], { month: "long", day: "numeric", year: "numeric" })
                  : "the end of your billing period"}
              </span>
              . You'll keep full access until then.
            </div>
            <div className="flex flex-col gap-2">
              <button
                onClick={undo}
                disabled={busy}
                data-testid="cancel-modal-undo"
                className="w-full py-2.5 rounded-md bg-emerald-700 text-white text-sm font-semibold hover:bg-emerald-800 disabled:bg-slate-400"
              >
                {busy ? "Restoring…" : "Changed my mind — restore"}
              </button>
              <button
                onClick={onClose}
                className="w-full py-2 text-xs text-slate-500 hover:text-slate-900"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
